/**
 * The palette's own list of addable node types, each with a minimal
 * schema-shaped default config — not necessarily schema-VALID (a few
 * placeholder values, like an unresolved `asset_id`, will still show up as
 * Inspector diagnostics; that is the existing diagnostics panel doing its
 * job, not a bug in this list) but the right shape so a newly added node
 * is a small edit away from valid rather than an empty, meaningless `{}`.
 *
 * Deliberately excludes two of the schema's node types:
 * - `start`: a document has exactly one, matching `start_node_id`, by
 *   construction; adding a second can never produce a valid document.
 * - `amd_gate`: plan step 3 (docs/plans/06) — "mark AMD gate
 *   system-managed" — is a separate, later designer slice. Nothing here
 *   prevents an amd_gate node existing in a document already (the shipped
 *   outbound examples have one), only from being ADDED fresh through this
 *   palette before that protection exists.
 */
export interface NodeTemplate {
  type: string;
  label: string;
  defaultConfig: () => unknown;
}

export const ADDABLE_NODE_TEMPLATES: NodeTemplate[] = [
  { type: "play", label: "Play (audio asset)", defaultConfig: () => ({ asset_id: "new-asset" }) },
  {
    type: "tts",
    label: "TTS (text-to-speech)",
    defaultConfig: () => ({ text: "New message.", voice: "v1", language: "en-US" }),
  },
  {
    type: "gather",
    label: "Gather (DTMF menu)",
    defaultConfig: () => ({
      valid_keys: ["1"],
      timeout_ms: 5000,
      retries: 0,
      prompt: { kind: "audio", asset_id: "new-asset" },
    }),
  },
  {
    type: "branch",
    label: "Branch",
    defaultConfig: () => ({ cases: [{ id: "case_1", variable: "variable_name", operator: "exists" }] }),
  },
  {
    type: "repeat",
    label: "Repeat",
    defaultConfig: () => ({ trigger_keys: ["*"], max_repeats: 3 }),
  },
  {
    type: "consent",
    label: "Consent",
    defaultConfig: () => ({ action: "opt_out", channel: "voice", purpose: "utility_notifications" }),
  },
  { type: "tag", label: "Tag", defaultConfig: () => ({ tags: [] }) },
  {
    type: "http",
    label: "HTTP",
    defaultConfig: () => ({ endpoint_id: "new-endpoint", method: "GET", timeout_ms: 2000, response_mappings: [] }),
  },
  {
    type: "transfer",
    label: "Transfer",
    defaultConfig: () => ({ destination: "+15550100000", timeout_ms: 5000 }),
  },
  { type: "hangup", label: "Hangup", defaultConfig: () => ({ reason: "normal" }) },
];
